import { useMemo, useRef, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import clsx from 'clsx';
import { Calendar, ChevronDown, MapPin, Search } from 'lucide-react';
import { LoadingSpinner } from '@/shared/components/feedback/LoadingSpinner/LoadingSpinner';
import { todayInputValue } from '@/shared/utils/date-input';
import type { EventOption } from '../../../../types/campaigns.types';
import { hasEventEnded, sortEventsForPicker } from './EventPicker.logic';
import styles from './EventPicker.module.scss';

interface EventPickerProps {
  /** Full list of events from the organization. */
  events: EventOption[] | undefined;
  /** Query state — drives the loading spinner inside the dropdown. */
  isLoading: boolean;
  /** Currently selected event id (if any). */
  selectedEventId?: string;
  /** Current event name (from a previously selected event), for display. */
  eventName: string;
  /**
   * Fired when the user picks an event from the dropdown. The caller is
   * responsible for updating wizard form state (id/name/venue + dates).
   */
  onSelectEvent: (event: EventOption) => void;
}

/**
 * Combobox-style event picker. Lists upcoming events from the organizer's
 * org and lets them auto-populate the campaign with event name/venue/dates.
 * Events that have already ended are excluded — a campaign can't flight
 * against a date that's already passed.
 */
export function EventPicker({
  events,
  isLoading,
  selectedEventId,
  eventName,
  onSelectEvent,
}: EventPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selectedEvent = useMemo(
    () => events?.find((e) => e.id === selectedEventId),
    [events, selectedEventId],
  );

  // Recomputed per render rather than memoized: it's a single Date read, and
  // pinning it would go stale in a tab left open across midnight.
  const today = todayInputValue();

  const filtered = useMemo(() => {
    if (!events) return [];
    const upcoming = events.filter((evt) => !hasEventEnded(evt, today));
    const q = query.trim().toLowerCase();
    const matches = q
      ? upcoming.filter((evt) => {
          const hay = `${evt.name} ${evt.venue ?? ''}`.toLowerCase();
          return hay.includes(q);
        })
      : upcoming;
    return sortEventsForPicker(matches);
  }, [events, query, today]);

  const buttonLabel = selectedEvent?.name || eventName || 'Select an event...';

  return (
    <div className={styles.container}>
      <label className={styles.label}>Event</label>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            ref={triggerRef}
            type="button"
            className={clsx(styles.trigger, !selectedEvent && !eventName && styles.triggerEmpty)}
            aria-haspopup="listbox"
          >
            <span className={styles.triggerLabel}>{buttonLabel}</span>
            <ChevronDown size={16} className={styles.triggerIcon} />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className={styles.content}
            align="start"
            sideOffset={4}
            style={{ width: triggerRef.current?.offsetWidth }}
            onOpenAutoFocus={(e) => {
              // Focus the search input, not the first list item.
              e.preventDefault();
              const input = (e.currentTarget as HTMLElement).querySelector('input');
              (input as HTMLInputElement | null)?.focus();
            }}
          >
            <div className={styles.searchRow}>
              <Search size={14} className={styles.searchIcon} />
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Search events..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>

            <div className={styles.list} role="listbox">
              {isLoading ? (
                <div className={styles.state}>
                  <LoadingSpinner />
                  <span>Loading events...</span>
                </div>
              ) : filtered.length === 0 ? (
                <div className={styles.state}>
                  <span>
                    {query
                      ? `No events match "${query}".`
                      : 'No upcoming events found in your organization.'}
                  </span>
                </div>
              ) : (
                filtered.map((evt) => {
                  const isSelected = evt.id === selectedEventId;
                  return (
                    <button
                      key={evt.id}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={clsx(styles.item, isSelected && styles.itemSelected)}
                      onClick={() => {
                        onSelectEvent(evt);
                        setOpen(false);
                      }}
                    >
                      <span className={styles.itemName}>{evt.name}</span>
                      <span className={styles.itemMeta}>
                        {evt.venue && (
                          <span className={styles.itemMetaItem}>
                            <MapPin size={12} />
                            {evt.venue}
                          </span>
                        )}
                        {evt.startDate && (
                          <span className={styles.itemMetaItem}>
                            <Calendar size={12} />
                            {formatShortDate(evt.startDate)}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}

function formatShortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
